import type { ApproveDesignResult, RetryDesignResult } from './api'
import { marketplaceOfCabinet, parseVisual } from './visualMeta'

export interface PublicationApprovalItem {
  id: string
  designCode?: string | null
}

export interface PublicationApprovalState<T extends PublicationApprovalItem> {
  items: T[]
  origin: 'viewer' | 'list'
}

export interface PublicationScopeItem extends PublicationApprovalItem {
  products?: Array<{ article: string; imageUrls: string[] }>
  productArticle?: string
  marketplace?: string
  cabinet?: string
}

export interface PublicationTargetScope {
  article: string
  marketplace: string
  cabinet: string
}

export interface PublicationDesignScope {
  designCode: string
  targets: PublicationTargetScope[]
}

export interface PublicationFailure {
  designCode: string
  message: string
}

export interface PublicationBatchResult {
  succeeded: string[]
  failed: PublicationFailure[]
}

type ApproveDesign = (
  designCode: string,
  options: { publishConfirmed: boolean; dryRun: boolean; expectedRunId?: string },
) => Promise<ApproveDesignResult>

export type RetryPublicationDecision = 'confirm_publication' | 'retry_started'

type RequestPublishApproval<T extends PublicationApprovalItem> = (
  item: T,
  origin: 'viewer' | 'list',
) => void

function itemKey(item: PublicationApprovalItem): string {
  return String(item.designCode || item.id).trim()
}

function targetKey(target: PublicationTargetScope): string {
  return JSON.stringify([
    target.article.trim(),
    target.marketplace.toLowerCase(),
    target.cabinet.toLowerCase(),
  ])
}

/**
 * Строит доступный оператору scope из канонических строк вкладки. Backend всё
 * равно публикует полный ready scope макета; этот список ничего не фильтрует.
 */
export function buildPublicationDesignScopes(
  designCodes: string[],
  items: PublicationScopeItem[],
): PublicationDesignScope[] {
  const normalizedCodes = [...new Set(designCodes.map((code) => code.trim()).filter(Boolean))]

  return normalizedCodes.map((designCode) => {
    const targets: PublicationTargetScope[] = []
    const seenTargets = new Set<string>()
    const addTarget = (target: PublicationTargetScope) => {
      const normalized = {
        article: target.article.trim(),
        marketplace: target.marketplace.trim().toUpperCase(),
        cabinet: target.cabinet.trim(),
      }
      if (!normalized.article || !normalized.marketplace || !normalized.cabinet) return
      const key = targetKey(normalized)
      if (seenTargets.has(key)) return
      seenTargets.add(key)
      targets.push(normalized)
    }

    for (const item of items) {
      if (itemKey(item) !== designCode) continue

      for (const product of item.products ?? []) {
        const cabinets = new Set(
          (product.imageUrls ?? []).map((url) => parseVisual(url).cabinet),
        )
        for (const cabinet of cabinets) {
          addTarget({
            article: product.article,
            marketplace: marketplaceOfCabinet(cabinet),
            cabinet,
          })
        }
      }

      if (item.productArticle && item.marketplace && item.cabinet) {
        addTarget({
          article: item.productArticle,
          marketplace: item.marketplace,
          cabinet: item.cabinet,
        })
      }
    }

    return { designCode, targets }
  })
}

/** React вызывает этот updater последовательно даже для синхронного bulk-цикла. */
export function accumulatePublicationApproval<T extends PublicationApprovalItem>(
  current: PublicationApprovalState<T> | null,
  item: T,
  origin: 'viewer' | 'list',
): PublicationApprovalState<T> {
  if (!current) return { items: [item], origin }
  const key = itemKey(item)
  if (current.items.some((queued) => itemKey(queued) === key)) return current
  return { ...current, items: [...current.items, item] }
}

/** Backend не имеет права повторять внешнюю публикацию без нового решения человека. */
export function retryPublicationDecision(result: RetryDesignResult): RetryPublicationDecision {
  return result.requires_confirmation === true
    && result.confirmation === 'marketplace_publication'
    ? 'confirm_publication'
    : 'retry_started'
}

/** Контроллерный seam: тот же маршрут используется страницей и компонентным тестом. */
export function routeRetryPublication<T extends PublicationApprovalItem>(
  result: RetryDesignResult,
  item: T,
  requestPublishApproval: RequestPublishApproval<T>,
): RetryPublicationDecision {
  const decision = retryPublicationDecision(result)
  if (decision === 'confirm_publication') {
    requestPublishApproval(item, 'viewer')
  }
  return decision
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

const aggregateCountKeys = ['processed', 'published', 'failed', 'already_published'] as const

type ParsedCounts = {
  processed: number
  published: number
  failed: number
  alreadyPublished: number
}

function parseCounts(value: unknown): ParsedCounts | string {
  if (!isRecord(value)) return 'Некорректный итог публикации: counts должен быть объектом'
  const invalidCounts = aggregateCountKeys.filter((key) => {
    const count = value[key]
    return !Number.isInteger(count) || Number(count) < 0
  })
  if (invalidCounts.length > 0) {
    return `Некорректный итог публикации: невалидные счётчики ${invalidCounts.join(', ')}`
  }
  const processed = value.processed as number
  const published = value.published as number
  const failed = value.failed as number
  const alreadyPublished = value.already_published as number
  if (published + failed + alreadyPublished !== processed) {
    return (
      'Некорректный итог публикации: ' +
      `processed=${processed}, published=${published}, failed=${failed}, already=${alreadyPublished}`
    )
  }
  return { processed, published, failed, alreadyPublished }
}

function knownOutcome(value: unknown): value is NonNullable<ApproveDesignResult['outcome']> {
  return value === 'published'
    || value === 'partial'
    || value === 'already_published'
    || value === 'preview'
    || value === 'success'
}

function outcomeFailure(
  outcome: NonNullable<ApproveDesignResult['outcome']>,
  counts: ParsedCounts,
  reconciliation?: unknown,
): string | null {
  const countDetails =
    `processed=${counts.processed}, published=${counts.published}, ` +
    `failed=${counts.failed}, already=${counts.alreadyPublished}`
  const reconciliationFailed = isRecord(reconciliation) && reconciliation.failed !== undefined
    ? reconciliation.failed
    : undefined
  if (
    reconciliationFailed !== undefined
    && (!Number.isInteger(reconciliationFailed) || Number(reconciliationFailed) < 0)
  ) {
    return 'Некорректный итог публикации: невалидный счётчик reconciliation.failed'
  }
  if (outcome === 'partial' || counts.failed > 0 || Number(reconciliationFailed) > 0) {
    const reconciliationDetails = isRecord(reconciliation)
      ? `, reconciliation_failed=${String(reconciliationFailed ?? '—')}` +
          (Array.isArray(reconciliation.errors) && reconciliation.errors.length > 0
            ? `, reconciliation_errors=${reconciliation.errors.map(String).join(' | ')}`
            : '')
      : ''
    return `Частичная публикация: outcome=${outcome}, ${countDetails}${reconciliationDetails}`
  }
  if (outcome === 'preview') {
    return `Публикация не выполнена: backend вернул preview для подтверждённого запроса, ${countDetails}`
  }
  return null
}

function legacyPublishFailure(publish: Record<string, unknown>): string | null {
  const presentCountKeys = aggregateCountKeys.filter((key) => publish[key] !== undefined)
  const invalidCounts = presentCountKeys.filter((key) => {
    const value = publish[key]
    return !Number.isInteger(value) || Number(value) < 0
  })
  if (invalidCounts.length > 0) {
    return `Некорректный итог публикации: невалидные счётчики ${invalidCounts.join(', ')}`
  }
  const failed = typeof publish.failed === 'number' ? publish.failed : 0
  if (failed > 0) {
    return (
      'Частичная публикация legacy-ответа: ' +
      `published=${publish.published ?? '—'}, failed=${failed}, ` +
      `already=${publish.already_published ?? '—'}`
    )
  }
  if (presentCountKeys.length === aggregateCountKeys.length) {
    const parsed = parseCounts(publish)
    if (typeof parsed === 'string') return parsed
  }
  return null
}

function aggregateFailure(result: ApproveDesignResult): string | null {
  if (!isRecord(result)) return null

  const topLevelOutcome: unknown = result.outcome
  if (topLevelOutcome !== undefined && topLevelOutcome !== null) {
    if (!knownOutcome(topLevelOutcome)) {
      return `Некорректный итог публикации: неизвестный outcome=${String(topLevelOutcome)}`
    }
    if (topLevelOutcome === 'preview' && result.counts === undefined) {
      return 'Публикация не выполнена: backend вернул preview для подтверждённого запроса'
    }
    const parsed = parseCounts(result.counts)
    if (typeof parsed === 'string') return parsed
    return outcomeFailure(topLevelOutcome, parsed, result.reconciliation)
  }

  if (!Object.prototype.hasOwnProperty.call(result, 'publish')) {
    // Старый backend возвращал fulfilled-ответ без aggregate/outcome.
    return null
  }
  const publish = result.publish
  if (!isRecord(publish)) {
    return 'Некорректный итог публикации: publish должен быть объектом'
  }

  const nestedOutcome = publish.outcome
  if (nestedOutcome === undefined || nestedOutcome === null) {
    return legacyPublishFailure(publish)
  }
  if (!knownOutcome(nestedOutcome)) {
    return `Некорректный итог публикации: неизвестный outcome=${String(nestedOutcome)}`
  }
  const parsed = parseCounts(publish)
  if (typeof parsed === 'string') return parsed
  return outcomeFailure(nestedOutcome, parsed)
}

function publicationRequestError(reason: unknown): string {
  const error = reason as any
  const message = String(
    error?.response?.data?.error?.message
      || error?.response?.data?.message
      || error?.message
      || reason
      || 'неизвестная ошибка',
  )
  if (message.includes('PUBLICATION_RUN_CHANGED')) {
    return (
      'Прогон публикации изменился. Закройте подтверждение, заново откройте его '
      + 'и пересмотрите frozen payload перед новой попыткой.'
    )
  }
  return message
}

/** Выполняет по одному запросу на макет с зафиксированным expected ready run. */
export async function publishConfirmedDesigns(
  items: PublicationApprovalItem[],
  expectedRunIds: Readonly<Record<string, string>>,
  approveDesign: ApproveDesign,
): Promise<PublicationBatchResult> {
  const uniqueItems: PublicationApprovalItem[] = []
  const seenDesigns = new Set<string>()
  for (const item of items) {
    const designCode = String(item.designCode || '').trim()
    if (!designCode) throw new Error('У макета нет design_code')
    if (seenDesigns.has(designCode)) continue
    seenDesigns.add(designCode)
    uniqueItems.push(item)
  }
  const missingExpectedRuns = uniqueItems
    .map((item) => String(item.designCode || '').trim())
    .filter((designCode) => !String(expectedRunIds[designCode] || '').trim())
  if (missingExpectedRuns.length > 0) {
    throw new Error(
      `Публикация заблокирована: нет expected_run_id для макетов ${missingExpectedRuns.join(', ')}`,
    )
  }

  const results = await Promise.allSettled(
    uniqueItems.map(async (item) => {
      const designCode = String(item.designCode || '').trim()
      const approval = await approveDesign(designCode, {
        publishConfirmed: true,
        dryRun: false,
        expectedRunId: expectedRunIds[designCode].trim(),
      })
      const failure = aggregateFailure(approval)
      if (failure) throw new Error(failure)
      return designCode
    }),
  )

  const succeeded: string[] = []
  const failed: PublicationFailure[] = []
  results.forEach((result, index) => {
    const designCode = String(
      uniqueItems[index]?.designCode || uniqueItems[index]?.id || '',
    ).trim()
    if (result.status === 'fulfilled') {
      succeeded.push(result.value)
    } else {
      failed.push({
        designCode,
        message: publicationRequestError(result.reason),
      })
    }
  })

  return { succeeded, failed }
}
